import { forwardRef, type ReactNode } from 'react';

import { useBehavioralTrackingProps } from '../../../core/hooks/useBehavioralTrackingProps.js';
import type { AriaLabelingProps } from '../../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../../core/types/data-props.js';
import type { DOMProps } from '../../../core/types/dom.js';
import type { MaskingProps } from '../../../core/types/masking-props.js';
import type { StylingProps } from '../../../core/types/styling-props.js';
import { mergeProps } from '../../../core/utils/merge-props.js';
import { Content as NotificationSettingsContent } from '../../../notifications/notification-settings/components/Content.js';

//#region Typings
/** @public */
export interface NotifyButtonContentProps
  extends
    DOMProps,
    AriaLabelingProps,
    StylingProps,
    MaskingProps,
    DataTestId,
    BehaviorTrackingProps {
  children?:
    | (({
        disabled,
        headingText,
        infoText,
        hasReadPermission,
        hasWritePermission,
      }: {
        disabled: boolean;
        headingText: string;
        infoText: string;
        hasReadPermission: boolean;
        hasWritePermission: boolean;
      }) => ReactNode)
    | ReactNode;
}
//#endregion Typings

/**
 * The content of the NotifyButton overlay.
 * @public
 */
export const Content = forwardRef<HTMLDivElement, NotifyButtonContentProps>(
  (props, ref) => {
    const { children, ...remainingProps } = props;

    const [behavioralTrackingProps, restProps] =
      useBehavioralTrackingProps(remainingProps);

    return (
      <NotificationSettingsContent
        ref={ref}
        {...mergeProps(restProps, behavioralTrackingProps)}
      >
        {children}
      </NotificationSettingsContent>
    );
  },
);

(Content as typeof Content & { displayName: string }).displayName =
  'NotifyButton.Content';
